/**
 * ================================================================================================
 *  THE CHOSEN COMPANY, REMEMBERED ACROSS A PAGE LOAD. **Owner section 3, 2026-10-28.**
 * ================================================================================================
 *
 *     *"בחירת „בדיקות" בבורר העליון החליפה את דף הבית במסך כמעט ריק. לחיצה על „עמוד הבית"
 *     החזירה את הכרטיסים ואיפסה את הבורר ל„בחירת חברה" … החברה הנבחרת תישמר במעבר בין מסכים
 *     וברענון, כל עוד ההרשאה תקפה."*
 *
 * ── WHY A STORE AND NOT REACT STATE, WHICH IS WHAT IT WAS ───────────────────────────────────────
 *
 * `ContextProvider` held the chosen company in `useState`. That is correct for a single-page app
 * and this is not one in practice: every navigation link in this shell is a plain `<a href>`, so
 * the browser performs a FULL PAGE LOAD and the entire React tree — the chosen company with it — is
 * destroyed and rebuilt. The reset the Owner saw was not a bug in the picker; it was the picker
 * faithfully rendering a brand-new store. Nothing in the app ever "cleared" it.
 *
 * So the choice has to live somewhere a page load cannot reach. `localStorage`, with the same
 * reasoning and the same shape as `favourites.ts`: there is no `user.preferences.update` route, so
 * this is per device and does not follow the person to another machine. **That limitation is the
 * Owner's own separation** — *"שמירת סדר בין מכשירים תירשם כהרחבה נפרדת"* — and the same applies
 * here: remembering it on the server is a route and a column, which is a product change.
 *
 * ── IT IS A PREFERENCE, NEVER AN AUTHORISATION ──────────────────────────────────────────────────
 *
 * What is stored is a `company_uid` and nothing else — no name, no grant, no capability. The value
 * is only ever USED by matching it against `ctx.visibleCompanies`, which is the server's
 * grant-filtered answer from `companies.list.read`. A uid whose grant was revoked therefore
 * resolves to nothing on the next load without this file needing to know that, and every screen it
 * reaches still passes gate stage 4 on the server. A remembered uid can no more open a company than
 * a typed one can.
 *
 * The stale key is deliberately NOT deleted when it fails to match: a grant that comes back should
 * restore the person's last choice, and a read-time comparison against the live list is already the
 * whole control.
 *
 * Every read and write is wrapped. Storage throws in a private window and answers nothing with site
 * data blocked, and a remembered company must never be the reason a screen fails to render — the
 * private window is exactly where the Owner was testing.
 */

const KEY = 'glpoint.company';

export function readChosenCompany(): string | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw === null || raw === '' ? null : raw;
  } catch {
    // Private window, or site data blocked. "Nobody has chosen" is a working screen.
    return null;
  }
}

/** `null` forgets it — which is what a tenant switch means, and nothing else does. */
export function writeChosenCompany(companyUid: string | null): void {
  try {
    if (companyUid === null) window.localStorage.removeItem(KEY);
    else window.localStorage.setItem(KEY, companyUid);
  } catch {
    // The choice still applies to this page; it simply will not survive the next load.
  }
}
